import React, { useState, useEffect } from 'react';
import { X, Flame, Users } from 'lucide-react';
import { Button } from './ui/button';
import axios from '../config/axios';
import { trackEvent } from '../utils/analytics';

/**
 * HotSeatPrompt - Non-invasive slide-in prompt for artists to post to Hot Seat
 * 
 * Shows after successful submission redirect to Public Session.
 * Only visible to the submitting artist (based on submission data in URL params).
 */
const HotSeatPrompt = ({ 
  submissionData, 
  sessionUrl,
  onDismiss,
  onPosted 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [posted, setPosted] = useState(false);

  useEffect(() => {
    if (!submissionData?.email) return;
    
    // Check if artist has Cafe posting permission
    const checkPermission = async () => {
      try {
        const response = await axios.get(`/api/waitlist/check-cafe-permission?email=${encodeURIComponent(submissionData.email)}`);
        setHasPermission(response.data.hasPermission);
        // Show prompt after a brief delay for smooth UX
        setTimeout(() => setIsVisible(true), 1500);
      } catch (error) {
        console.error('Failed to check Cafe permission:', error);
        setHasPermission(false);
        setTimeout(() => setIsVisible(true), 1500);
      }
    };
    
    checkPermission();
  }, [submissionData?.email]);

  const handlePostToHotSeat = async () => {
    if (!submissionData) return;
    
    setIsPosting(true);
    try {
      const postData = {
        submission_id: submissionData.id,
        track_title: submissionData.title,
        artist_name: submissionData.artist,
        artist_email: submissionData.email,
        genre: submissionData.genre,
        audio_url: submissionData.file_url || null,
        streaming_link: submissionData.streaming_link || null,
        session_url: sessionUrl
      };
      
      const response = await axios.post('/api/forum/posts/hot-seat-auto', postData);
      
      if (response.data.success) {
        // Track hot seat post for analytics
        trackEvent('hot_seat_post', { 
          song_title: submissionData.song_title,
          artist_name: submissionData.artist_name,
          genre: submissionData.genre
        });
        
        setPosted(true);
        if (onPosted) onPosted(response.data.post);
        // Auto-hide after success
        setTimeout(() => {
          setIsVisible(false);
          if (onDismiss) onDismiss();
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to post to Hot Seat:', error);
    } finally {
      setIsPosting(false);
    }
  };

  const handleDismiss = () => {
    setIsMinimized(true);
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const handleJoinWaitlist = () => {
    window.open('/artist-waitlist', '_blank');
  };

  if (!isVisible || !submissionData) return null;

  // Minimized state - show small centered button
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center px-4">
        <button
          onClick={handleRestore}
          className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg transition-all hover:scale-105"
        >
          <Flame className="w-4 h-4" />
          <span className="text-sm font-medium">Post to Hot Seat</span>
        </button>
      </div>
    );
  }

  // Success state
  if (posted) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-80 animate-in slide-in-from-right duration-300">
        <div className="bg-green-600 text-white rounded-xl shadow-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Flame className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold">Posted to Hot Seat! 🔥</p>
              <p className="text-sm text-white/80">The community can now listen</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main prompt - approved artist
  if (hasPermission) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-80 animate-in slide-in-from-right duration-300">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-xl shadow-2xl overflow-hidden">
          {/* Header with dismiss */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              <span className="text-xs font-medium text-orange-400 uppercase tracking-wide">Hot Seat</span>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Content */}
          <div className="px-4 pb-4">
            <h3 className="font-semibold text-lg mb-1">
              You&apos;re up next. Want the community listening too?
            </h3>
            <p className="text-sm text-gray-300 mb-4">
              Your track is now in the review queue. Post it to the Hot Seat so artists and reviewers can tap in while you wait.
            </p>
            
            {/* Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handlePostToHotSeat}
                disabled={isPosting}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium"
              >
                {isPosting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span> Posting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Flame className="w-4 h-4" /> Post to Hot Seat
                  </span>
                )}
              </Button>
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="text-gray-400 hover:text-white hover:bg-gray-700"
              >
                Maybe later
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not approved - show waitlist CTA
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 animate-in slide-in-from-right duration-300">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header with dismiss */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-xs font-medium text-blue-400 uppercase tracking-wide">Millionaire Cafe</span>
          </div>
          <button 
            onClick={() => { setIsVisible(false); if (onDismiss) onDismiss(); }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-4 pb-4">
          <h3 className="font-semibold text-lg mb-1">
            Want your track featured in the Hot Seat?
          </h3>
          <p className="text-sm text-gray-300 mb-4">
            Join the Artist Waitlist to unlock posting access in the Millionaire Cafe.
          </p>
          
          {/* Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleJoinWaitlist}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-medium"
            >
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Join Artist Waitlist
              </span>
            </Button>
            <Button
              onClick={() => { setIsVisible(false); if (onDismiss) onDismiss(); }}
              variant="ghost"
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotSeatPrompt;
